import React, { useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Calculator, ChevronDown, Sparkles, User, X } from 'lucide-react';
import { ALL_NAV_SECTIONS, isNavChildActive, type NavItem } from '@/config/navigation';
import {
  AI_ASSISTANT_SHORTCUT,
  useAccountingShellOptional,
} from '@/components/layout/AccountingShellProvider';

interface SidebarProps {
  open?: boolean;
  onClose?: () => void;
  onOpenCalculator?: () => void;
}

/** الگوی archive: دسکتاپ ثابت، موبایل drawer از راست + overlay */
const Sidebar: React.FC<SidebarProps> = ({ open = true, onClose, onOpenCalculator }) => {
  const location = useLocation();
  const shell = useAccountingShellOptional();
  const [expandedGroups, setExpandedGroups] = React.useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (onClose) onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- بستن drawer پس از ناوبری؛ همان الگوی archive
  }, [location.pathname]);

  useEffect(() => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      for (const sec of ALL_NAV_SECTIONS) {
        for (const item of sec.items) {
          if (!item.children?.length) continue;
          if (isNavChildActive(location.pathname, item)) {
            next.add(item.to);
          }
        }
      }
      return next;
    });
  }, [location.pathname]);

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `flex w-full items-center gap-3 rounded-lg px-4 py-2.5 text-sm transition-all hover:bg-white/10 ${
      isActive
        ? 'border-r-4 border-amber-400 bg-white/10 font-medium text-white'
        : 'border-r-4 border-transparent text-gray-200'
    }`;

  const subNavClass = ({ isActive }: { isActive: boolean }) =>
    `flex w-full items-center rounded-lg py-2 pe-3 ps-8 text-sm transition-all hover:bg-white/10 ${
      isActive ? 'font-medium text-amber-200' : 'text-gray-300'
    }`;

  const toggleGroup = (key: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const renderNavItem = (item: NavItem, sectionTitle: string) => {
    const Icon = item.icon;

    if (item.children?.length) {
      const groupOpen = expandedGroups.has(item.to) || isNavChildActive(location.pathname, item);
      const parentLooksActive = isNavChildActive(location.pathname, item);

      return (
        <div key={`${sectionTitle}-${item.to}-group`} className="mb-0.5">
          <button
            type="button"
            onClick={() => toggleGroup(item.to)}
            className={`flex w-full items-center gap-3 rounded-lg px-4 py-2.5 text-sm transition-all hover:bg-white/10 ${
              parentLooksActive
                ? 'border-r-4 border-amber-400/70 bg-white/5 font-medium text-white'
                : 'border-r-4 border-transparent text-gray-200'
            }`}
          >
            <Icon size={18} className="shrink-0 opacity-95" />
            <span className="min-w-0 flex-1 text-start">{item.label}</span>
            <ChevronDown
              size={16}
              className={`shrink-0 opacity-80 transition-transform ${groupOpen ? '-rotate-180' : ''}`}
            />
          </button>
          {groupOpen ? (
            <div className="mt-0.5 space-y-0.5 border-r border-white/15 pr-1 mr-5">
              {item.children.map((child) => (
                <NavLink
                  key={`${item.to}-${child.to}-${child.label}`}
                  to={child.to}
                  end={child.end}
                  className={subNavClass}
                >
                  {child.label}
                </NavLink>
              ))}
            </div>
          ) : null}
        </div>
      );
    }

    return (
      <NavLink
        key={`${sectionTitle}-${item.to}-${item.label}`}
        to={item.to}
        end={item.end}
        className={navClass}
      >
        <Icon size={18} className="shrink-0 opacity-95" />
        <span>{item.label}</span>
      </NavLink>
    );
  };

  return (
    <>
      {open && onClose && (
        <div className="fixed inset-0 z-40 bg-black/50 md:hidden" onClick={onClose} aria-hidden="true" />
      )}
      <aside
        className={`
          flex w-64 shrink-0 flex-col overflow-hidden bg-slate-800 text-white
          fixed inset-y-0 right-0 z-50 md:relative md:z-auto
          transform transition-transform duration-300 ease-out
          ${open ? 'translate-x-0' : 'translate-x-full md:translate-x-0'}
        `}
        dir="rtl"
      >
        <div className="flex shrink-0 items-center justify-between border-b border-white/10 p-4">
          <div>
            <h2 className="text-lg font-bold">حسابداری</h2>
            <p className="mt-0.5 text-xs text-gray-400">دفتر، اسناد، گزارش</p>
          </div>
          {onClose && (
            <button type="button" onClick={onClose} className="rounded-lg p-2 hover:bg-white/10 md:hidden" aria-label="بستن منو">
              <X size={20} />
            </button>
          )}
        </div>

        <nav className="sidebar-nav min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-2 py-3">
          {ALL_NAV_SECTIONS.map((section) => (
            <div key={section.title} className="mb-4">
              <div className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-gray-400">{section.title}</div>
              {section.items.map((item) => renderNavItem(item, section.title))}
            </div>
          ))}

          <div className="mb-4">
            <div className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-gray-400">ابزارها</div>
            <button
              type="button"
              onClick={() => shell?.openAiAssistant()}
              className="mb-0.5 flex w-full items-center gap-3 rounded-lg border-r-4 border-transparent px-4 py-2.5 text-sm text-gray-200 transition-all hover:bg-white/10"
            >
              <Sparkles size={18} className="shrink-0 opacity-95" />
              <span className="min-w-0 flex-1 text-start">هوش مصنوعی</span>
              <kbd className="rounded bg-white/10 px-1.5 py-0.5 text-[10px] font-mono text-gray-300">
                {AI_ASSISTANT_SHORTCUT}
              </kbd>
            </button>
            <button
              type="button"
              onClick={() => onOpenCalculator?.()}
              className="flex w-full items-center gap-3 rounded-lg border-r-4 border-transparent px-4 py-2.5 text-sm text-gray-200 transition-all hover:bg-white/10"
            >
              <Calculator size={18} className="shrink-0 opacity-95" />
              <span>ماشین حساب</span>
            </button>
          </div>
        </nav>

        <div className="border-t border-white/10 p-3">
          <NavLink
            to="/profile"
            className={({ isActive }) =>
              `flex shrink-0 items-center gap-2 rounded-lg px-2 py-2 transition-colors ${
                isActive ? 'bg-white/10 text-white' : 'text-gray-300 hover:bg-white/10'
              }`
            }
          >
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/15">
              <User size={18} />
            </div>
            <span className="truncate text-sm">پنل مالی</span>
          </NavLink>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
